import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../scss/CraneZoneMonitoring.scss';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faMapMarkerAlt, 
  faExclamationTriangle, 
  faWeightHanging,
  faHome,
  faSearchPlus,
  faSearchMinus,
  faMap,
  faThLarge,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faCircle,
  faBan,
  faTimes,
  faSave,
  faSyncAlt,
  faEdit,
  faPlay,
  faStop,
  faUser,
  faSignOutAlt,
  faCaretDown,
  faInfoCircle,
  faExclamationCircle,
  faUserCircle,
  faCrown
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';

const CraneZoneMonitoring = () => {
    const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [craneState, setCraneState] = useState({
    bridge_position: 0,
    trolley_position: 0,
    hoist_position: 5,
    current_operation: null,
    current_zone: { name: 'Main Work Area', type: 'work', is_restricted: false },
    current_load: 0,
    capacity: 5,
    status: 'operational',
    health_score: 100
  });
  const [zones, setZones] = useState([]);
  const [selectedZoneType, setSelectedZoneType] = useState('work');
  const [zoneFormData, setZoneFormData] = useState({
    name: '',
    type: 'work',
    x: 0,
    y: 0,
    z: 0,
    width: 10,
    depth: 10,
    height: 5,
    safety_param: 'none'
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showZoneAlert, setShowZoneAlert] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  
  // Refs for 3D
  const viewerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const bridgeRef = useRef(null);
  const trolleyRef = useRef(null);
  const hookRef = useRef(null);
  const hookLineRef = useRef(null);
  const trolleyWheelsRef = useRef([]);
  const zoneMeshesRef = useRef([]);
  const animationIdRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Constants
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000;
  const CRANE_LIMITS = {
    bridge: { min: -20, max: 20 },
    trolley: { min: -0.8, max: 0.8 },
    hoist: { min: 2, max: 8 }
  };
  
  // Helper Functions
  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);
  
  // WebSocket Connection
  const initWebSocket = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/data/zone-monitoring/`;
    
    const socket = new WebSocket(wsUrl);
    
    socket.onopen = () => {
      if (!isMountedRef.current) return;
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      setReconnectAttempts(0);
      
      // Send initial ping
      socket.send(JSON.stringify({ type: 'ping' }));
      
      // Request initial data
      socket.send(JSON.stringify({ type: 'get_crane_state' }));
      socket.send(JSON.stringify({ type: 'get_zones' }));
      socket.send(JSON.stringify({ type: 'get_real_time_data' }));
      socket.send(JSON.stringify({ type: 'start_position_tracking' }));
    };
    
    socket.onmessage = (event) => {
      if (!isMountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (isMountedRef.current) {
        setIsConnected(false);
      }
    };
    
    socket.onclose = () => {
      if (!isMountedRef.current) return;
      console.log('WebSocket closed');
      setIsConnected(false);
      
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS && isMountedRef.current) {
        setTimeout(() => {
          if (isMountedRef.current) {
            setReconnectAttempts(prev => prev + 1);
            initWebSocket();
          }
        }, RECONNECT_DELAY);
      }
    };
    
    setWs(socket);
  }, [reconnectAttempts]);
  
  // Handle WebSocket Messages
  const handleWebSocketMessage = useCallback((data) => {
    switch(data.type) {
      case 'connection_established':
        showNotification('success', `Connected to crane: ${data.crane_id}`);
        break;
        
      case 'crane_state':
        setCraneState(prev => ({ ...prev, ...data.data }));
        update3DModelPositions(data.data);
        break;
        
      case 'realtime_data':
        setCraneState(prev => ({
          ...prev,
          bridge_position: data.data.positions.bridge,
          trolley_position: data.data.positions.trolley,
          hoist_position: data.data.positions.hoist,
          current_load: data.data.load_data?.current || 0,
          health_score: data.data.health_score,
          current_zone: data.data.zone_info
        }));
        updateZoneDisplay(data.data.zone_info);
        break;
        
      case 'zones':
        setZones(data.data);
        update3DZones(data.data);
        break;
        
      case 'position_update':
        update3DModelPositions(data.data);
        break;
        
      case 'success':
        showNotification('success', data.message);
        break;
        
      case 'error':
        showNotification('error', data.message);
        break;
        
      default:
        break;
    }
  }, [showNotification]);
  
  // Send Operation
  const sendOperation = useCallback((operation) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'send_operation',
        operation: operation
      }));
    } else {
      showNotification('error', 'Not connected to crane');
    }
  }, [ws, showNotification]);
  
  // 3D Viewer Functions
  const init3DViewer = useCallback(() => {
    if (!viewerRef.current || !isMountedRef.current) return;
    
    // Clean up any existing renderer
    if (rendererRef.current && viewerRef.current.contains(rendererRef.current.domElement)) {
      viewerRef.current.removeChild(rendererRef.current.domElement);
    }
    
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;
    
    // Camera
    const camera = new THREE.PerspectiveCamera(75, viewerRef.current.clientWidth / viewerRef.current.clientHeight, 0.1, 1000);
    camera.position.set(30, 30, 30);
    cameraRef.current = camera;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(viewerRef.current.clientWidth, viewerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    viewerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    
    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.screenSpacePanning = true;
    controlsRef.current = controls;
    
    // Lights
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);
    
    const backLight = new THREE.DirectionalLight(0xffffff, 0.5);
    backLight.position.set(-1, 1, -1);
    scene.add(backLight);
    
    // Fill light
    const fillLight = new THREE.PointLight(0x404040, 0.3);
    fillLight.position.set(0, 5, 0);
    scene.add(fillLight);
    
    // Grid
    const gridHelper = new THREE.GridHelper(50, 50);
    scene.add(gridHelper);
    
    // Axes helper (optional - can be removed in production)
    const axesHelper = new THREE.AxesHelper(25);
    scene.add(axesHelper);
    
    // Create crane model
    createCraneModel();
    
    // Animation loop
    const animate = () => {
      if (!isMountedRef.current) return;
      animationIdRef.current = requestAnimationFrame(animate);
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();
  }, []);
  
  const createCraneModel = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    // Runway beams
    const runwayGeometry = new THREE.BoxGeometry(50, 0.5, 0.5);
    const runwayMaterial = new THREE.MeshPhongMaterial({ color: 0x555555 });
    
    const leftRunway = new THREE.Mesh(runwayGeometry, runwayMaterial);
    leftRunway.position.set(0, 10, -1.5);
    scene.add(leftRunway);
    
    const rightRunway = new THREE.Mesh(runwayGeometry, runwayMaterial);
    rightRunway.position.set(0, 10, 1.5);
    scene.add(rightRunway);
    
    // Bridge
    const bridgeGeometry = new THREE.BoxGeometry(3, 0.3, 0.3);
    const bridgeMaterial = new THREE.MeshPhongMaterial({ color: 0x3498db });
    const bridge = new THREE.Mesh(bridgeGeometry, bridgeMaterial);
    bridge.position.set(0, 10, 0);
    scene.add(bridge);
    bridgeRef.current = bridge;
    
    // Trolley platform
    const trolleyGeometry = new THREE.BoxGeometry(1.2, 0.2, 0.8);
    const trolleyMaterial = new THREE.MeshPhongMaterial({ color: 0x2980b9 });
    const trolley = new THREE.Mesh(trolleyGeometry, trolleyMaterial);
    trolley.position.set(0, 9.7, 0);
    scene.add(trolley);
    trolleyRef.current = trolley;
    
    // Trolley wheels
    const wheelGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.1, 16);
    const wheelMaterial = new THREE.MeshPhongMaterial({ color: 0x333333 });
    const wheelPositions = [
      { x: 0.4, z: 0.4 },
      { x: -0.4, z: 0.4 },
      { x: 0.4, z: -0.4 },
      { x: -0.4, z: -0.4 }
    ];
    
    wheelPositions.forEach(pos => {
      const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(pos.x, 9.85, pos.z);
      scene.add(wheel);
      trolleyWheelsRef.current.push(wheel);
    });
    
    // Hook
    const hookGeometry = new THREE.SphereGeometry(0.3, 16, 16);
    const hookMaterial = new THREE.MeshPhongMaterial({ color: 0xe74c3c });
    const hook = new THREE.Mesh(hookGeometry, hookMaterial);
    hook.position.set(0, 5, 0);
    scene.add(hook);
    hookRef.current = hook;
    
    // Hook line
    updateHookLine();
  }, []);
  
  const updateHookLine = useCallback(() => {
    const scene = sceneRef.current;
    const trolley = trolleyRef.current;
    const hook = hookRef.current;
    
    if (!scene || !trolley || !hook) return;
    
    if (hookLineRef.current) {
      scene.remove(hookLineRef.current);
      hookLineRef.current = null;
    }
    
    const points = [
      new THREE.Vector3(trolley.position.x, trolley.position.y + 0.3, trolley.position.z),
      new THREE.Vector3(trolley.position.x, hook.position.y, trolley.position.z)
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0x000000 });
    const hookLine = new THREE.Line(geometry, material);
    scene.add(hookLine);
    hookLineRef.current = hookLine;
  }, []);
  
  const update3DModelPositions = useCallback((stateData) => {
    if (!bridgeRef.current || !trolleyRef.current || !hookRef.current) return;
    
    const bridgeTarget = stateData.bridge_position || 0;
    const trolleyTarget = stateData.trolley_position || 0;
    const hoistTarget = stateData.hoist_position || 5;
    
    // Apply limits
    const bridgeLimited = Math.max(CRANE_LIMITS.bridge.min, Math.min(CRANE_LIMITS.bridge.max, bridgeTarget));
    const trolleyLimited = Math.max(CRANE_LIMITS.trolley.min, Math.min(CRANE_LIMITS.trolley.max, trolleyTarget));
    const hoistLimited = Math.max(CRANE_LIMITS.hoist.min, Math.min(CRANE_LIMITS.hoist.max, hoistTarget));
    
    // Update bridge
    bridgeRef.current.position.x = bridgeLimited;
    
    // Update trolley
    trolleyRef.current.position.x = bridgeLimited;
    trolleyRef.current.position.z = trolleyLimited;
    
    // Update trolley wheels
    trolleyWheelsRef.current.forEach((wheel, index) => {
      const wheelPos = [
        { x: 0.4, z: 0.4 },
        { x: -0.4, z: 0.4 },
        { x: 0.4, z: -0.4 },
        { x: -0.4, z: -0.4 }
      ][index];
      if (wheel) {
        wheel.position.x = bridgeLimited + wheelPos.x;
        wheel.position.z = trolleyLimited + wheelPos.z;
      }
    });
    
    // Update hook
    hookRef.current.position.x = bridgeLimited;
    hookRef.current.position.z = trolleyLimited;
    hookRef.current.position.y = hoistLimited;
    
    // Update hook line
    updateHookLine();
  }, [updateHookLine]);
  
  const update3DZones = useCallback((zonesData) => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    // Clear existing zones
    zoneMeshesRef.current.forEach(mesh => {
      if (mesh && scene.contains(mesh)) {
        scene.remove(mesh);
      }
    });
    zoneMeshesRef.current = [];
    
    // Create new zones
    zonesData.forEach(zone => {
      const zoneGeometry = new THREE.BoxGeometry(
        zone.dimensions.width,
        zone.dimensions.height,
        zone.dimensions.depth
      );
      
      let color;
      switch(zone.type) {
        case 'work': color = 0x00ff00; break;
        case 'restricted': color = 0xff0000; break;
        case 'warning': color = 0xffff00; break;
        default: color = 0x00ff00;
      }
      
      const zoneMaterial = new THREE.MeshPhongMaterial({
        color: color,
        transparent: true,
        opacity: 0.2,
        side: THREE.DoubleSide
      });
      
      const zoneMesh = new THREE.Mesh(zoneGeometry, zoneMaterial);
      zoneMesh.position.set(
        zone.position.x,
        zone.position.y,
        zone.position.z
      );
      
      scene.add(zoneMesh);
      zoneMeshesRef.current.push(zoneMesh);
    });
  }, []);
  
  const updateZoneDisplay = useCallback((zoneInfo) => {
    if (!zoneInfo) return;
    setShowZoneAlert(zoneInfo.type === 'restricted');
  }, []);
  
  // Handle Zone Form Submission
  const handleZoneSubmit = useCallback((e) => {
    e.preventDefault();
    
    const newZone = {
      name: zoneFormData.name,
      type: zoneFormData.type,
      position: {
        x: parseFloat(zoneFormData.x),
        y: parseFloat(zoneFormData.y),
        z: parseFloat(zoneFormData.z)
      },
      dimensions: {
        width: parseFloat(zoneFormData.width),
        depth: parseFloat(zoneFormData.depth),
        height: parseFloat(zoneFormData.height)
      },
      safety_param: zoneFormData.safety_param
    };
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'create_zone',
        zone: newZone
      }));
      showNotification('success', 'Zone created successfully');
      
      // Reset form
      setZoneFormData({
        name: '',
        type: 'work',
        x: 0,
        y: 0,
        z: 0,
        width: 10,
        depth: 10,
        height: 5,
        safety_param: 'none'
      });
    }
  }, [zoneFormData, ws, showNotification]);
  
  // Handle Window Resize
  const handleResize = useCallback(() => {
    if (viewerRef.current && cameraRef.current && rendererRef.current && isMountedRef.current) {
      const width = viewerRef.current.clientWidth;
      const height = viewerRef.current.clientHeight;
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    }
  }, []);
  
  // Viewer Controls
  const resetView = useCallback(() => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(30, 30, 30);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  }, []);
  
  const setTopView = useCallback(() => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(0, 50, 0.1);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  }, []);
  
  const setSideView = useCallback(() => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(50, 10, 0);
      controlsRef.current.target.set(0, 10, 0);
      controlsRef.current.update();
    }
  }, []);
  
  const zoomIn = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.fov = Math.max(20, cameraRef.current.fov / 1.1);
      cameraRef.current.updateProjectionMatrix();
    }
  }, []);
  
  const zoomOut = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.fov = Math.min(120, cameraRef.current.fov * 1.1);
      cameraRef.current.updateProjectionMatrix();
    }
  }, []);
  
  // Cleanup function
  const cleanup3D = useCallback(() => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }
    
    if (rendererRef.current) {
      rendererRef.current.dispose();
      if (viewerRef.current && viewerRef.current.contains(rendererRef.current.domElement)) {
        viewerRef.current.removeChild(rendererRef.current.domElement);
      }
      rendererRef.current = null;
    }
    
    // Clear references
    sceneRef.current = null;
    cameraRef.current = null;
    bridgeRef.current = null;
    trolleyRef.current = null;
    hookRef.current = null;
    hookLineRef.current = null;
    trolleyWheelsRef.current = [];
    zoneMeshesRef.current = [];
  }, []);
  
  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (isMountedRef.current && viewerRef.current) {
        init3DViewer();
      }
    }, 100);
    
    initWebSocket();
    window.addEventListener('resize', handleResize);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      cleanup3D();
      if (ws) {
        ws.close();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [initWebSocket, init3DViewer, handleResize, cleanup3D]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      sessionStorage.clear();
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };
  
  return (
    <div className="crane-zone-monitoring">
        <div className="dashboard-content">
          {/* Header */}
          <div className="header">
            <div className="header-left">
              <div className="header-logo">EOT Crane Zone Control</div>
            </div>
            
            <div className="user-info-wrapper">
                        <div className="account-dropdown">
                          <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                            {orgLogo ? (
                                              <img
                                                src={orgLogo}
                                                alt="Organization Logo"
                                                style={{
                                                  width: '32px',
                                                  height: '32px',
                                                  borderRadius: '50%',
                                                  objectFit: 'cover',
                                                  border: '2px solid #4361ee'
                                                }}
                                                onError={() => setOrgLogo(null)}
                                              />
                                            ) : (
                                              <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                            )}
                            <span>{sessionStorage.getItem('username') || 'User'}</span>
                            <FontAwesomeIcon icon={faCaretDown} />
                          </div>
                          {isDropdownOpen && (
                            <div className="account-menu show">
                              <div className="account-menu-item plan-info">
                                <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                <span>Basic Plan</span>
                              </div>
                              <div className="account-menu-divider"></div>
                              <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faUser} /> Profile
                              </button>
                              <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                              </button>
                              <div className="account-menu-divider"></div>
                              <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
          </div>
          
          {/* Page Title */}
          <div className="page-title">
            <h1>CraneIQ Gateway - Zone Monitoring</h1>
            <p>Real-time 3D visualization and control of restricted zones for overhead crane operations</p>
          </div>
          
          {/* Crane Status Cards */}
          <div className="crane-statusa">
            <div className={`status-cards ${craneState.status === 'operational' ? 'status-operational' : 'status-danger'}`}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <div className="label">Crane Status</div>
              <div className="value">{craneState.status === 'operational' ? 'Operational' : 'Error'}</div>
            </div>
            <div className="status-cards">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <div className="label">Current Zone</div>
              <div className="value">{craneState.current_zone?.name || 'Main Work Area'}</div>
            </div>
            <div className={`status-cards ${craneState.current_zone?.type === 'restricted' ? 'status-danger' : ''}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div className="label">Nearest Restricted</div>
              <div className="value">{craneState.current_zone?.type === 'restricted' ? 'IN RESTRICTED ZONE!' : '25.0m E'}</div>
            </div>
            <div className="status-cards">
              <FontAwesomeIcon icon={faWeightHanging} />
              <div className="label">Current Load</div>
              <div className="value">{craneState.current_load} / {craneState.capacity} tons</div>
            </div>
          </div>
          
          {/* 3D Viewer */}
          <div className="viewer-container">
            <div ref={viewerRef} id="crane3dViewer" style={{ width: '100%', height: '100%' }}></div>
            
            <div className="viewer-controls">
              <button className="viewer-btn" onClick={resetView} title="Reset View">
                <FontAwesomeIcon icon={faHome} />
              </button>
              <button className="viewer-btn" onClick={zoomIn} title="Zoom In">
                <FontAwesomeIcon icon={faSearchPlus} />
              </button>
              <button className="viewer-btn" onClick={zoomOut} title="Zoom Out">
                <FontAwesomeIcon icon={faSearchMinus} />
              </button>
              <button className="viewer-btn" onClick={setTopView} title="Top View">
                <FontAwesomeIcon icon={faMap} />
              </button>
              <button className="viewer-btn" onClick={setSideView} title="Side View">
                <FontAwesomeIcon icon={faThLarge} />
              </button>
            </div>
            
            {/* Crane Movement Controls */}
            <div className="crane-controls">
              <div className="control-group">
                <h4>Bridge (LT)</h4>
                <div className="horizontal-controls">
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Lt_Forward')}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Lt_Reverse')}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                </div>
                <div className="control-value">{craneState.bridge_position.toFixed(2)} m</div>
              </div>
              
              <div className="control-group">
                <h4>Trolley (CT)</h4>
                <div className="horizontal-controls">
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Ct_Left')}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Ct_Right')}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
                <div className="control-value">{craneState.trolley_position.toFixed(2)} m</div>
              </div>
              
              <div className="control-group">
                <h4>Hoist</h4>
                <div className="vertical-controls">
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Hoist_Up')}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button 
                    className="control-btn" 
                    onClick={() => sendOperation('Hoist_Down')}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                </div>
                <div className="control-value">{craneState.hoist_position.toFixed(2)} m</div>
              </div>
            </div>
            
            {/* Connection Status */}
            <div className={`data-status ${isConnected ? 'connected' : 'disconnected'}`}>
              <FontAwesomeIcon icon={faCircle} className={isConnected ? 'connected-icon' : 'disconnected-icon'} />
              {isConnected ? 'Receiving live data' : 'Connecting to backend...'}
            </div>
            
            {/* Zone Alert */}
            {showZoneAlert && (
              <div id="zoneAlert" className="zone-alert show">
                <strong>⚠️ RESTRICTED ZONE!</strong>
              </div>
            )}
          </div>
          
          {/* Zone Controls */}
          <div className="zone-controls">
            <div className="zone-configuration">
              <div className="zone-control-header">
                <div className="zone-control-title">Zone Configuration</div>
              </div>
              
              <div className="zone-types">
                {['work', 'restricted', 'warning'].map(type => (
                  <div 
                    key={type}
                    className={`zone-type ${type} ${selectedZoneType === type ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedZoneType(type);
                      setZoneFormData(prev => ({ ...prev, type }));
                    }}
                  >
                    <FontAwesomeIcon icon={type === 'work' ? faCheckCircle : type === 'restricted' ? faBan : faExclamationTriangle} />
                    <div>{type.charAt(0).toUpperCase() + type.slice(1)} Zone</div>
                  </div>
                ))}
              </div>
              
              <form className="zone-form" onSubmit={handleZoneSubmit}>
                <div className="form-group">
                  <label className="form-label">Zone Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="Main Work Area" 
                    value={zoneFormData.name}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Zone Type</label>
                  <select 
                    className="form-select"
                    value={zoneFormData.type}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="work">Work Zone</option>
                    <option value="restricted">Restricted Zone</option>
                    <option value="warning">Warning Zone</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">X Position (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="0" 
                    step="0.1"
                    value={zoneFormData.x}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, x: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Y Position (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="0" 
                    step="0.1"
                    value={zoneFormData.y}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, y: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Z Position (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="0" 
                    step="0.1"
                    value={zoneFormData.z}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, z: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Length (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="10" 
                    step="0.1"
                    value={zoneFormData.width}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, width: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Width (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="10" 
                    step="0.1"
                    value={zoneFormData.depth}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, depth: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Height (m)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="5" 
                    step="0.1"
                    value={zoneFormData.height}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, height: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label className="form-label">Safety Parameters</label>
                  <select 
                    className="form-select"
                    value={zoneFormData.safety_param}
                    onChange={(e) => setZoneFormData(prev => ({ ...prev, safety_param: e.target.value }))}
                  >
                    <option value="none">No restrictions</option>
                    <option value="slow">Reduce speed</option>
                    <option value="stop">Complete stop</option>
                    <option value="alarm">Sound alarm</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setZoneFormData({
                    name: '',
                    type: 'work',
                    x: 0,
                    y: 0,
                    z: 0,
                    width: 10,
                    depth: 10,
                    height: 5,
                    safety_param: 'none'
                  })}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FontAwesomeIcon icon={faSave} /> Save Zone
                  </button>
                </div>
              </form>
            </div>
            
            <div className="zone-list">
              <div className="zone-control-header">
                <div className="zone-control-title">Active Zones</div>
                <div>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => ws?.send(JSON.stringify({ type: 'get_zones' }))}
                  >
                    <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                  </button>
                </div>
              </div>
              
              <table className="zone-table">
                <thead>
                  <tr>
                    <th>Zone ID</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Position</th>
                    <th>Dimensions</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map(zone => (
                    <tr key={zone.zone_id}>
                      <td>{zone.zone_id}</td>
                      <td>{zone.name}</td>
                      <td>
                        <span className={`zone-status status-${zone.type}`}>
                          {zone.type.charAt(0).toUpperCase() + zone.type.slice(1)}
                        </span>
                      </td>
                      <td>{zone.position?.x},{zone.position?.y},{zone.position?.z}</td>
                      <td>{zone.dimensions?.width}×{zone.dimensions?.depth}×{zone.dimensions?.height}m</td>
                      <td>
                        <button className="btn btn-secondary" style={{ padding: '4px 8px' }}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Real-time Position Tracking */}
          <div className="position-tracker">
            <div className="zone-control-header">
              <div className="zone-control-title">Real-time Position Tracking</div>
              <div>
                <button 
                  className="btn btn-primary" 
                  onClick={() => sendOperation(craneState.current_operation === 'Start' ? 'Stop' : 'Start')}
                >
                  <FontAwesomeIcon icon={craneState.current_operation === 'Start' ? faStop : faPlay} /> 
                  {craneState.current_operation === 'Start' ? 'Stop Crane' : 'Start Crane'}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => ws?.send(JSON.stringify({ type: 'get_crane_state' }))}
                >
                  <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                </button>
              </div>
            </div>
            
            <div className="position-values">
              <div className="position-value">
                <div className="position-label">Bridge Position (X)</div>
                <div className="position-number">
                  {craneState.bridge_position.toFixed(2)} <span className="position-unit">m</span>
                </div>
              </div>
              <div className="position-value">
                <div className="position-label">Trolley Position (Z)</div>
                <div className="position-number">
                  {craneState.trolley_position.toFixed(2)} <span className="position-unit">m</span>
                </div>
              </div>
              <div className="position-value">
                <div className="position-label">Hook Height (Y)</div>
                <div className="position-number">
                  {craneState.hoist_position.toFixed(2)} <span className="position-unit">m</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      
      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <div className="notification-content">
            <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : notification.type === 'error' ? faExclamationCircle : faInfoCircle} />
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CraneZoneMonitoring;